import './bootstrap';
import '../css/app.css';

import VueGoogleMaps from '@fawmi/vue-google-maps';
import {createApp, h} from 'vue';
import {createInertiaApp, Link, Head, router} from '@inertiajs/vue3';
import {modal} from 'momentum-modal';
import {resolvePageComponent} from 'laravel-vite-plugin/inertia-helpers';
import {createPinia} from 'pinia';
import {translations} from './Mixins/translations';
import {ZiggyVue} from '../../vendor/tightenco/ziggy/dist/vue.m';
import AppLayout from './Layouts/AppLayout.vue';
import ApplyLayout from './Layouts/ApplyLayout.vue';
import VueGtag from 'vue-gtag';
import VueTippy from 'vue-tippy';
import Notifications from 'notiwind';
import * as Sentry from '@sentry/vue';

const appName = '360AI';

const formatPageTitle = title => {
  if (!title) {
    return appName;
  }

  return title.includes(appName) ? title : `${title} - ${appName}`;
};

/**
 * History API Compatibility Fix for Vue 3 + Inertia.js
 *
 * Vue 3 wraps all reactive data in Proxy objects. When Inertia.js calls
 * history.replaceState() to save scroll positions, the browser's structured
 * clone algorithm fails because it cannot clone Proxy objects.
 *
 * This patch converts Vue Proxies to plain objects before storing in history.
 * Can be removed after upgrading to Inertia v2 if the issue is fixed there.
 *
 * @see https://github.com/inertiajs/inertia/issues/775
 */
(function patchHistoryApiForVueProxies() {
  const originalReplaceState = history.replaceState.bind(history);
  const originalPushState = history.pushState.bind(history);

  const toPlainObject = state => {
    if (state === null || state === undefined) return state;
    try {
      return JSON.parse(JSON.stringify(state));
    } catch {
      return state;
    }
  };

  history.replaceState = (state, unused, url) => {
    originalReplaceState(toPlainObject(state), unused, url);
  };

  history.pushState = (state, unused, url) => {
    originalPushState(toPlainObject(state), unused, url);
  };
})();

window.dump = window.log = function (...data) {
  data.forEach(function (element) {
    console.log(element);
  });
};

if (process.env.NODE_ENV === 'production') {
  router.on('navigate', event => {
    gtag('js', new Date());
    gtag('config', 'G-DYZL0DK866');
    gtag('config', 'GT-TWRL58KL');
  });

  router.on('navigate', event => {
    if (event?.detail?.page?.url === '/verify/email') {
      gtag('js', new Date());
      gtag('config', 'GT-TWRL58KL');
    }
  });
}

createInertiaApp({
  title: formatPageTitle,
  progress: {
    color: '#4B5563',
  },
  resolve: name =>
    resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')).then(page => {
      // Use ApplyLayout for apply pages, AppLayout for everything else
      if (name.startsWith('Apply/')) {
        page.default.layout ??= ApplyLayout;
      } else {
        page.default.layout ??= AppLayout;
      }

      return page;
    }),
  setup({el, App, props, plugin}) {
    const app = createApp({render: () => h(App, props)})
      .use(modal, {
        resolve: name => resolvePageComponent(name, import.meta.glob('./Pages/**/*.vue')),
      })
      .use(createPinia())
      .use(plugin)
      .use(VueGoogleMaps, {
        load: {
          key: 'AIzaSyDk8JkWOdaouMIiRVG3ispwIdEv3bYFWMQ',
        },
      })
      .use(VueGtag, {
        config: {id: 'GT-TWRL58KL'},
      })
      .use(ZiggyVue, Ziggy)
      .use(Notifications)
      .use(VueTippy, {
        directive: 'tooltip',
        component: 'tooltip',
        componentSingleton: 'tooltip-singleton', // => <tippy-singleton/>,
        defaultProps: {
          placement: 'left',
          allowHTML: true,
        },
      })
      .component('Link', Link)
      .component('Head', Head)
      .mixin({
        methods: {
          route,
        },
      })
      .mixin(translations);

    const dsn = import.meta.env.VITE_SENTRY_DSN;
    const env = import.meta.env.VITE_APP_ENV || 'local';

    if (dsn) {
      Sentry.init({
        app,
        dsn,
        environment: env,
        sendDefaultPii: true,
        enableLogs: true,
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        integrations: [
          Sentry.browserTracingIntegration({traceFetch: true, traceXHR: true}),
          Sentry.replayIntegration(),
        ],
      });

      const user = props?.initialPage?.props?.auth?.user;
      if (user) {
        Sentry.setUser({
          id: user.id,
          email: user.email,
          name: user.name,
        });
      }
    }

    return app.mount(el);
  },
});
